import api from './api';

/**
 * #690: Reconciliation > POSP payouts. Match bank debits to POSP payouts (suggest, then a person
 * confirms), queues, cash receipts and the per-POSP position. Amounts are API decimal strings.
 */

export type QueueName = 'pending' | 'not_in_bank' | 'unknown_debits' | 'partial' | 'bulk' | 'cash_no_receipt' | 'confirmed';
export type PayoutState = 'pending' | 'not_in_bank' | 'cash_no_receipt' | 'part_confirmed' | 'confirmed' | 'void';

export const QUEUE_LABEL: Record<QueueName, string> = {
  pending: 'Pending',
  not_in_bank: 'Completed, not in bank',
  unknown_debits: 'Bank debits with no payout',
  partial: 'Part paid',
  bulk: 'Bulk transfers',
  cash_no_receipt: 'Cash, no receipt',
  confirmed: 'Confirmed',
};
export const QUEUE_ORDER: QueueName[] = ['not_in_bank', 'unknown_debits', 'partial', 'cash_no_receipt', 'pending', 'bulk', 'confirmed'];

export const STATE_LABEL: Record<PayoutState, string> = {
  pending: 'Pending',
  not_in_bank: 'Not in bank',
  cash_no_receipt: 'Cash, no receipt',
  part_confirmed: 'Part confirmed',
  confirmed: 'Confirmed',
  void: 'Void',
};

export type QueueCounts = Record<QueueName, number>;

export interface PayoutMatch {
  id: string;
  kind: 'bank' | 'cash_receipt';
  payout: string;
  payout_reference?: string;
  amount: string;
  reference: string;
  paid_on: string | null;
  bank_line: string | null;
  note: string;
}

export interface PayoutRow {
  id: string;
  payout_reference: string;
  posp_code: string;
  posp_name: string;
  period_start: string;
  period_end: string;
  status: string;
  payment_mode: string;
  payment_reference: string;
  payment_date: string | null;
  net_amount: string;
  confirmed: string;
  unconfirmed: string;
  state: PayoutState;
  bank_references: string[];
  matches: PayoutMatch[];
}

export interface DebitRow {
  id: string;
  bank_account_name: string;
  txn_date: string;
  narration: string;
  reference: string;
  debit: string;
  match_state: string;
  payouts?: PayoutMatch[];
}

export interface Suggestion {
  score: number;
  reasons: string[];
  kind: 'single' | 'bulk';
  total: string;
  payouts: { payout: string; payout_reference: string; posp_code: string; posp_name: string; net_amount: string; amount: string }[];
}

export interface Position {
  posp: string;
  posp_code: string;
  posp_name: string;
  earned: string;
  clawback: string;
  tds: string;
  ledger_paid: string;
  paid_in_bank: string;
  ledger_balance: string;
  payable: string;
  difference: string;
  unconfirmed_payouts: number;
}

export interface QueuePage<T> {
  count: number;
  results: T[];
}

const BASE = '/commissions/payout-reconciliation';

export const payoutReconciliationService = {
  async counts(): Promise<QueueCounts> {
    return (await api.get(`${BASE}/queues/`)).data;
  },
  async queue<T = PayoutRow | DebitRow>(name: QueueName, posp?: string): Promise<QueuePage<T>> {
    return (await api.get(`${BASE}/queue/`, { params: { name, ...(posp ? { posp } : {}) } })).data;
  },
  async suggestions(lineId: string): Promise<Suggestion[]> {
    return (await api.get(`${BASE}/suggestions/${lineId}/`)).data;
  },
  async confirm(line: string, allocations: { payout: string; amount: string }[], note = '') {
    return (await api.post(`${BASE}/confirm/`, { line, allocations, note })).data;
  },
  async unmatch(line: string, reason: string) {
    return (await api.post(`${BASE}/unmatch/`, { line, reason })).data;
  },
  async receipt(input: { payout: string; amount: string; receipt_date: string; reference: string; note?: string }) {
    return (await api.post(`${BASE}/receipt/`, input)).data;
  },
  async removeReceipt(match: string, reason: string) {
    return (await api.post(`${BASE}/remove-receipt/`, { match, reason })).data;
  },
  async positions(): Promise<Position[]> {
    return (await api.get(`${BASE}/positions/`)).data;
  },
};
